var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { faChevronLeft, faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { IconButton, Link, Stack, Typography, styled } from '@mui/material';
import i18n from 'i18next';
import { isNumber } from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import yup from '../../config/yup';
import { PlanType } from '../../enum';
import { FormField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '../common';
import { useReportPlanType } from '../report/selectors';
import { ALL_LOCATIONS_ID, FULL_DATE_TIME_FORMAT, MAXIMUM_VALUES, METRIC_FORMATTING_OPTIONS, PERCENTAGES, REPORT_BASE_COLUMNS, REPORT_DAILY_COLUMNS, REPORT_WORK_OFFERED_HANDLED_COLUMNS, TIME_FORMAT, } from './constants';
import { StyledNativeNumberInput } from './dataPanel';
import { ACTUAL_DATA_FIELDS, DAILY_DATA_FIELDS, DAILY_FILTER_KEYS, METRICS_ENUM } from './enum';
import { formatDayOfWeek, getFormatterValueByMetric, usePlanColorById, useReportSummaryData, useReportTimelineOptions, useSelectIntervalTime, useUpdateActualDataManual, useVarianceColor, } from './selectors';
var StyledTableContainer = styled(TableContainer)(function () { return ({
    borderRadius: 0,
    maxHeight: '24.25rem',
}); });
var StyledStackButtonGroup = styled(Stack)(function (_a) {
    var theme = _a.theme;
    return ({
        flexDirection: 'row',
        gap: theme.spacing(2),
        alignItems: 'center',
        justifyContent: 'center',
        height: '1.375rem',
    });
});
var StyledTableHeadCellTitle = styled(TableCell)(function (_a) {
    var theme = _a.theme;
    return ({
        backgroundColor: theme.palette.text.primary,
        color: theme.palette.common.white,
        textAlign: 'center',
        fontWeight: 'bold',
        lineHeight: 'normal',
        padding: theme.spacing(2),
        textTransform: 'uppercase',
    });
});
var StyledTableHeadCell = styled(TableCell)(function (_a) {
    var theme = _a.theme;
    return ({
        color: theme.palette.common.white,
        textAlign: 'center',
        fontWeight: 'bold',
        lineHeight: 'normal',
        minWidth: '9.375rem',
        padding: theme.spacing(2, 0.25) + ' !important',
    });
});
var StyledTypographyTitle = styled(Typography)(function () { return ({
    height: '1.375rem',
    fontWeight: 'bold',
    textTransform: 'uppercase',
}); });
var StyledTableBodyCell = styled(TableCell)(function (_a) {
    var theme = _a.theme;
    return ({
        position: 'relative',
        lineHeight: 'normal',
        textAlign: 'center',
        color: theme.palette.common.black,
    });
});
var StyledStack = styled(Stack)(function (_a) {
    var theme = _a.theme;
    return ({
        width: '100%',
        height: '98%',
        position: 'absolute',
        top: 0,
        left: 0,
        padding: theme.spacing(0, 2),
        justifyContent: 'center',
        alignItems: 'center',
    });
});
var StyledDayStack = styled(Stack)(function () { return ({
    minWidth: '12.5rem',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
}); });
var StyledIconButton = styled(IconButton)(function () { return ({
    padding: 0,
    '&.Mui-disabled': {
        opacity: 0.3,
    },
}); });
var StyledIcon = styled(FontAwesomeIcon)(function (_a) {
    var theme = _a.theme;
    return ({
        color: theme.palette.common.white,
        width: '1rem',
        height: '1rem',
    });
});
var StyledButtonGroup = styled(Stack)(function (_a) {
    var theme = _a.theme;
    return ({
        marginTop: theme.spacing(4),
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-end',
        gap: theme.spacing(4),
    });
});
var StyledButton = styled(LoadingButton)(function () { return ({
    textTransform: 'uppercase',
    minWidth: '6.25rem',
}); });
export var hasWorkOfferedHandledError = function (rowData, columnIndex) {
    if (!Array.isArray(rowData) || rowData.length < 2)
        return false;
    var offered = rowData[0], handled = rowData[1];
    // Show error if one is filled but the other is not
    return ((offered != null && handled == null && columnIndex === 1) ||
        (handled != null && offered == null && columnIndex === 0));
};
export var DailyTableBodyRow = React.memo(function (_a) {
    var rowKey = _a.rowKey, rowValues = _a.values, columns = _a.columns, editableRows = _a.editableRows, metricTab = _a.metricTab, dailyViewType = _a.dailyViewType, specificDay = _a.specificDay, planId = _a.planId, onDateChange = _a.onDateChange, methods = _a.methods, _b = _a.disabled, disabled = _b === void 0 ? false : _b;
    var _c = usePlanColorById(planId || 0), bodyColor = _c[1];
    var getVarianceColor = useVarianceColor(metricTab);
    var getAbandonmentRateColor = useVarianceColor(METRICS_ENUM.ABANDONMENT_RATE);
    // Memoize helper functions to prevent re-creation on every render
    var getDefaultValueEditableCell = useCallback(function (value) {
        if (value && Object.keys(PERCENTAGES).includes(metricTab)) {
            return value * 100;
        }
        return value;
    }, [metricTab]);
    var getColorTableCell = useCallback(function (columnField, value) {
        var varianceStyledFields = [DAILY_DATA_FIELDS.VARIANCE, DAILY_DATA_FIELDS.VARIANCE_OFFERED];
        if (varianceStyledFields.includes(columnField)) {
            return getVarianceColor(value);
        }
        if (columnField === DAILY_DATA_FIELDS.ABANDONMENT_RATE) {
            return getAbandonmentRateColor(value);
        }
        return '';
    }, [getVarianceColor, getAbandonmentRateColor]);
    var getBackgroundTableCell = useCallback(function (theme, columnField, date) {
        if (columnField === DAILY_DATA_FIELDS.PLAN) {
            return bodyColor;
        }
        else if ([DAILY_DATA_FIELDS.ACTUAL, DAILY_DATA_FIELDS.ACTUAL_OFFERED, DAILY_DATA_FIELDS.ACTUAL_HANDLED].includes(columnField)) {
            var isFutureDate = date === null || date === void 0 ? void 0 : date.isAfter(moment.utc(), 'day');
            return isFutureDate ? theme.palette.action.disabledBackground : '';
        }
        return '';
    }, [bodyColor]);
    // Memoize the computed date value
    var computedDate = useMemo(function () {
        return dailyViewType === DAILY_FILTER_KEYS.DAILY_AVERAGE ? moment.utc(rowKey) : specificDay;
    }, [dailyViewType, rowKey, specificDay]);
    return (_jsx(TableRow, __assign({ hover: true }, { children: columns === null || columns === void 0 ? void 0 : columns.map(function (column, colIndex) {
            var _a;
            // Leading date/day/interval columns do not consume row values; align the
            // remaining cells by counting consuming columns before this one.
            var leadingColumns = columns.filter(function (col) { return col.type; }).length;
            var index = colIndex - leadingColumns;
            var isEditable = isNumber(column === null || column === void 0 ? void 0 : column.actualIndex) && (editableRows === null || editableRows === void 0 ? void 0 : editableRows.includes(rowKey));
            // Check if this field has error (for work_offered_handled metric)
            var rowData = metricTab === METRICS_ENUM.WORK_OFFERED_HANDLED && isEditable
                ? methods.watch("".concat(ACTUAL_DATA_FIELDS.UPSERT_DATASET, ".").concat(rowKey))
                : null;
            var hasError = hasWorkOfferedHandledError(rowData, (_a = column.actualIndex) !== null && _a !== void 0 ? _a : -1);
            return (_jsx(StyledTableBodyCell, __assign({ sx: function (theme) { return (__assign({ color: getColorTableCell(column.id, rowValues[index]), background: getBackgroundTableCell(theme, column.id, computedDate) }, (column.styled ? column.styled(theme) : {}))); } }, { children: isEditable ? (_jsx(StyledStack, { children: _jsx(FormField, __assign({ required: true, sx: { width: '100%' }, name: "".concat(ACTUAL_DATA_FIELDS.UPSERT_DATASET, ".").concat(rowKey, "[").concat(column.actualIndex, "]"), defaultValue: getDefaultValueEditableCell(rowValues[index]) }, { children: _jsx(StyledNativeNumberInput, __assign({}, METRIC_FORMATTING_OPTIONS[metricTab], MAXIMUM_VALUES[metricTab], { setValue: methods.setValue, disabled: !editableRows.includes(rowKey) || disabled, hasError: hasError })) })) })) : (_jsx(Typography, __assign({ component: "span", fontWeight: "inherit", lineHeight: "normal" }, (column.id === DAILY_DATA_FIELDS.DATE && { onClick: function () { return onDateChange(rowKey); } }), { children: column.render(rowKey, rowValues[index], metricTab) || '-' }))) }), column.id + rowValues[index]));
        }) }), metricTab + rowKey));
});
export var DailyTableHead = React.memo(function (_a) {
    var columns = _a.columns, onChange = _a.onChange;
    var t = useTranslation('report', { keyPrefix: 'actual.dailyCompareTable' }).t;
    var tLocales = useTranslation('translation', { keyPrefix: 'dateFormat' }).t;
    var watch = useFormContext().watch;
    var _b = watch(), dailyViewType = _b.dailyViewType, periodStart = _b.periodStart, periodEnd = _b.periodEnd, specificDay = _b.specificDay, planId = _b.planId;
    var headColor = usePlanColorById(planId || 0)[0];
    var planType = useReportPlanType();
    var timelines = useReportTimelineOptions();
    var startLimitDate = useMemo(function () {
        var _a;
        var startDateSelected = (_a = timelines.find(function (item) { return item.id === periodStart; })) === null || _a === void 0 ? void 0 : _a.moment;
        return moment(startDateSelected);
    }, [periodStart, timelines]);
    var endLimitDate = useMemo(function () {
        var _a;
        var endDateByPlan = planType === PlanType.Weekly ? periodEnd : periodStart;
        var endDateSelected = (_a = timelines.find(function (item) { return item.id === endDateByPlan; })) === null || _a === void 0 ? void 0 : _a.moment;
        if (planType === PlanType.Weekly) {
            return moment(endDateSelected).add(6, 'day');
        }
        return moment(endDateSelected).add(1, 'month').subtract(1, 'day');
    }, [periodEnd, timelines, planType, periodStart]);
    var formattedSpecificDay = useMemo(function () {
        if (!specificDay)
            return '';
        return moment(specificDay).format(tLocales('dayMonthYearFormat'));
    }, [specificDay, tLocales]);
    return (_jsxs(TableHead, { children: [_jsx(TableRow, { children: dailyViewType === DAILY_FILTER_KEYS.SPECIFIC_DAY ? (_jsx(TableCell, __assign({ colSpan: columns.length }, { children: _jsxs(StyledStackButtonGroup, { children: [_jsx(StyledIconButton, __assign({ "data-testid": "left-arrow", disabled: moment(specificDay).isSameOrBefore(startLimitDate, 'day'), onClick: function () { return onChange(moment(specificDay).add(-1, 'day')); } }, { children: _jsx(StyledIcon, { icon: faChevronLeft }) })), _jsx(StyledDayStack, { children: _jsx(StyledTypographyTitle, __assign({ sx: { textTransform: i18n.language === 'fr' ? 'none' : 'uppercase' } }, { children: "".concat(formatDayOfWeek(formattedSpecificDay), " ").concat(formattedSpecificDay) })) }), _jsx(StyledIconButton, __assign({ "data-testid": "right-arrow", disabled: moment(specificDay).isSameOrAfter(endLimitDate, 'day'), onClick: function () { return onChange(moment(specificDay).add(1, 'day')); } }, { children: _jsx(StyledIcon, { icon: faChevronRight }) }))] }) }))) : (_jsx(StyledTableHeadCellTitle, __assign({ colSpan: columns.length }, { children: _jsx(StyledTypographyTitle, { children: t('dailyAverage') }) }))) }), _jsx(TableRow, __assign({ sx: { height: '43px' } }, { children: columns.map(function (column) { return (_jsx(StyledTableHeadCell, __assign({ width: 100 / columns.length + '%', sx: { backgroundColor: "".concat(headColor, " !important") } }, { children: t(column.id) }), column.id)); }) }))] }));
});
export var DailyTableBody = React.memo(function (_a) {
    var _b;
    var methods = _a.methods, columns = _a.columns, data = _a.data, onChange = _a.onChange, requesting = _a.requesting, _c = _a.disabled, disabled = _c === void 0 ? false : _c;
    var t = useTranslation('translation').t;
    var _d = useFormContext(), watch = _d.watch, setValue = _d.setValue;
    var _e = watch(), metricTab = _e.metricTab, dailyViewType = _e.dailyViewType, specificDay = _e.specificDay, planId = _e.planId;
    var _f = data || { details: {}, editableRows: [] }, details = _f.details, editableRows = _f.editableRows;
    var onDateChange = React.useCallback(function (key) {
        setValue('dailyViewType', DAILY_FILTER_KEYS.SPECIFIC_DAY);
        onChange(moment.utc(key, t('dateFormat.dateTimeFormat')));
    }, [setValue, onChange, t]);
    return (_jsx(FormProvider, __assign({}, methods, { children: _jsx(TableBody, { children: details && Object.entries(details).length > 0 ? ((_b = Object.entries(details)) === null || _b === void 0 ? void 0 : _b.map(function (_a) {
                var key = _a[0], rowValues = _a[1];
                return (_jsx(DailyTableBodyRow, { rowKey: key, values: rowValues, columns: columns, editableRows: editableRows, metricTab: metricTab, dailyViewType: dailyViewType, specificDay: specificDay, planId: planId, onDateChange: onDateChange, methods: methods, disabled: disabled }, metricTab + key));
            })) : (_jsx(TableRow, { children: _jsx(TableCell, __assign({ sx: { height: '18.75rem' }, colSpan: columns.length }, { children: !requesting && t('noDataIndication') })) })) }) })));
});
export var getValidateForm = function (metricTab) {
    var _a;
    return yup.object((_a = {},
        _a[ACTUAL_DATA_FIELDS.UPSERT_DATASET] = yup
            .object()
            .test('validate-work-offered-handled', function (value) {
            if (metricTab !== METRICS_ENUM.WORK_OFFERED_HANDLED)
                return true;
            if (!value || typeof value !== 'object')
                return true;
            return Object.values(value).every(function (rowValues) {
                if (!Array.isArray(rowValues))
                    return true;
                var offered = rowValues[0], handled = rowValues[1];
                return (offered != null) === (handled != null);
            });
        })
            .required(),
        _a));
};
export var DailyCompareTable = function (_a) {
    var _b;
    var _c, _d;
    var requesting = _a.requesting, _e = _a.disabled, disabled = _e === void 0 ? false : _e, _f = _a.clearDisabled, clearDisabled = _f === void 0 ? disabled : _f, onOpenClearDialog = _a.onOpenClearDialog, onRefetchReport = _a.onRefetchReport;
    var t = useTranslation('report', { keyPrefix: 'actual.dailyCompareTable' }).t;
    var data = useReportSummaryData();
    var _g = useFormContext(), watch = _g.watch, setValue = _g.setValue;
    var _h = watch(), metricTab = _h.metricTab, dailyViewType = _h.dailyViewType, specificDay = _h.specificDay, queueId = _h.queueId, frequency = _h.frequency, queueLocationId = _h.queueLocationId;
    var isAbandonmentRateTab = metricTab === METRICS_ENUM.ABANDONMENT_RATE;
    var _j = useUpdateActualDataManual(), actualDataManualUpdated = _j[0], updateActualDataManual = _j[1], clearActualDataManual = _j[2];
    var intervalTime = useSelectIntervalTime(frequency, dailyViewType, queueId);
    var columns = useMemo(function () {
        var dailyAverageColumns = REPORT_DAILY_COLUMNS.filter(function (col) { return col.type === dailyViewType; });
        if (metricTab === METRICS_ENUM.WORK_OFFERED_HANDLED) {
            return __spreadArray(__spreadArray([], dailyAverageColumns, true), REPORT_WORK_OFFERED_HANDLED_COLUMNS.filter(function (column) {
                return queueLocationId === undefined || queueLocationId === -1 || column.id !== DAILY_DATA_FIELDS.ABANDONMENT_RATE;
            }), true);
        }
        return __spreadArray(__spreadArray([], dailyAverageColumns, true), REPORT_BASE_COLUMNS, true);
    }, [metricTab, dailyViewType, queueLocationId]);
    var initFormValues = useMemo(function () {
        var _a;
        if (!((_a = data === null || data === void 0 ? void 0 : data.editableRows) === null || _a === void 0 ? void 0 : _a.length) || !(data === null || data === void 0 ? void 0 : data.details))
            return {};
        var actualColumns = columns.filter(function (col) { return Number.isFinite(col.actualIndex); });
        return Object.fromEntries(data === null || data === void 0 ? void 0 : data.editableRows.filter(function (key) { return key in data.details; }).map(function (key) {
            var row = data === null || data === void 0 ? void 0 : data.details[key];
            var values = actualColumns.map(function (col) { var _a, _b; return (_b = getFormatterValueByMetric(row[((_a = col.actualIndex) !== null && _a !== void 0 ? _a : -1) + 1], metricTab)) !== null && _b !== void 0 ? _b : null; });
            return [key, values];
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data === null || data === void 0 ? void 0 : data.editableRows, data === null || data === void 0 ? void 0 : data.details]);
    var validationSchema = useMemo(function () { return getValidateForm(metricTab); }, [metricTab]);
    var dailyMethods = useForm({
        defaultValues: (_b = {}, _b[ACTUAL_DATA_FIELDS.UPSERT_DATASET] = initFormValues, _b),
        resolver: yupResolver(validationSchema),
    });
    var reset = dailyMethods.reset, dailyHandleSubmit = dailyMethods.handleSubmit, _k = dailyMethods.formState, isValid = _k.isValid, isDirty = _k.isDirty;
    useEffect(function () {
        var _a;
        if (requesting) {
            reset((_a = {}, _a[ACTUAL_DATA_FIELDS.UPSERT_DATASET] = initFormValues || {}, _a));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requesting]);
    var resetDailyMethods = useCallback(function () {
        var _a;
        var _b;
        if (!((_b = data === null || data === void 0 ? void 0 : data.editableRows) === null || _b === void 0 ? void 0 : _b.length) || !(data === null || data === void 0 ? void 0 : data.details))
            return;
        reset((_a = {}, _a[ACTUAL_DATA_FIELDS.UPSERT_DATASET] = initFormValues || {}, _a));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reset, data === null || data === void 0 ? void 0 : data.editableRows, data === null || data === void 0 ? void 0 : data.details, initFormValues]);
    useEffect(function () {
        resetDailyMethods();
        clearActualDataManual();
    }, [resetDailyMethods, clearActualDataManual]);
    var handleDateChange = function (key) {
        setValue('specificDay', key);
        onRefetchReport === null || onRefetchReport === void 0 ? void 0 : onRefetchReport();
    };
    var onSave = function (formData) {
        var upsert_dataset = formData[ACTUAL_DATA_FIELDS.UPSERT_DATASET];
        if (!upsert_dataset || Object.keys(upsert_dataset).length === 0)
            return;
        var isWorkOfferedHandled = metricTab === METRICS_ENUM.WORK_OFFERED_HANDLED;
        var isAverageMode = dailyViewType === DAILY_FILTER_KEYS.DAILY_AVERAGE;
        var createUpsertDataEntry = function (values, intervalStart) {
            var offered = values[0], handled = values[1];
            return isWorkOfferedHandled
                ? { offered: offered, handled: handled, interval_start: intervalStart }
                : { value: offered, interval_start: intervalStart };
        };
        var upsertData = Object.entries(upsert_dataset).map(function (_a) {
            var dateOrTimeStr = _a[0], values = _a[1];
            var intervalStart = isAverageMode
                ? moment(dateOrTimeStr).format(FULL_DATE_TIME_FORMAT)
                : moment(specificDay)
                    .hour(moment(dateOrTimeStr, TIME_FORMAT).hour())
                    .minute(moment(dateOrTimeStr, TIME_FORMAT).minute())
                    .second(0)
                    .format(FULL_DATE_TIME_FORMAT);
            var valuesFormatted = Object.keys(PERCENTAGES).includes(metricTab)
                ? values.map(function (val) { return (isNumber(val) ? val / 100 : val); })
                : values;
            return createUpsertDataEntry(valuesFormatted, intervalStart);
        });
        var payload = __assign({ interval_time: intervalTime, metric_type: metricTab, upsert_datas: upsertData }, (queueLocationId !== undefined && queueLocationId !== ALL_LOCATIONS_ID
            ? { queue_location_id: queueLocationId }
            : {}));
        updateActualDataManual(payload, { queueId: queueId });
    };
    return (_jsxs(Stack, { children: [_jsx(StyledTableContainer, { children: _jsxs(Table, __assign({ stickyHeader: true }, { children: [_jsx(DailyTableHead, { columns: columns, onChange: handleDateChange }), _jsx(DailyTableBody, { methods: dailyMethods, requesting: requesting, data: data, columns: columns, onChange: handleDateChange, disabled: disabled })] })) }), !isAbandonmentRateTab && (_jsxs(StyledButtonGroup, { children: [_jsx(Link, __assign({ component: "button", type: "button", onClick: resetDailyMethods, disabled: !isDirty || !((_c = data === null || data === void 0 ? void 0 : data.editableRows) === null || _c === void 0 ? void 0 : _c.length) || disabled }, { children: t('reset') })), _jsx(Link, __assign({ component: "button", onClick: onOpenClearDialog, type: "button", disabled: clearDisabled }, { children: t('clear') })), _jsx(StyledButton, __assign({ variant: "outlined", onClick: dailyHandleSubmit(onSave), disabled: !isValid || !isDirty || !((_d = data === null || data === void 0 ? void 0 : data.editableRows) === null || _d === void 0 ? void 0 : _d.length) || disabled, loading: actualDataManualUpdated.requesting }, { children: t('save') }))] }))] }));
};
